import { getRouteApi, Link } from "@tanstack/react-router";
import { buttonVariants } from "@workspace/web-ui/components/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import { cn } from "@workspace/web-ui/lib/utils";
import { FormattedEncounter } from "~/features/dashboard/pages/encounters/types";

const formatValue = (value: unknown): string => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value);
};

interface PatientDetailsTabsProps {
    encounter: FormattedEncounter;
    onOpenChange?: (open: boolean) => void;
}

export const PatientDetailsTabs = ({ encounter, onOpenChange }: PatientDetailsTabsProps) => {
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const { program } = routeApi.useParams();
    return (
      <div className="space-y-4 w-full">
        <div className="flex flex-col gap-3 w-full text-left rounded-[16px] border border-[#0404041A] pb-3">
            <Tabs defaultValue="patient-details">
            <TabsList>
                    <TabsTrigger value="patient-details" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-[#4A85FF] shadow-none focus:ring-0">Patient Details</TabsTrigger>
                    <TabsTrigger value="initial" className="text-wrap rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-[#4A85FF] shadow-none focus:ring-0">
                      <span className="hidden sm:inline">Initial Questions</span>
                      <span className="inline sm:hidden">Initial...</span>
                    </TabsTrigger> 
                    <TabsTrigger value="supplementary" className="text-wrap rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-b-[#4A85FF] shadow-none focus:ring-0">
                      <span className="hidden sm:inline">Supplementary Questions</span>
                      <span className="inline sm:hidden">Supplementary...</span>
                    </TabsTrigger>  
                </TabsList>
                <TabsContent value="patient-details" className="px-4 pb-4">
                    <div className="grid grid-cols-[min-content_1fr] text-sm gap-8">
                        <div className="flex flex-col gap-2">
                            {encounter?.patient.gender && <p>Gender</p>}
                            {encounter?.patient.age && <p>Age</p>}
                            {encounter?.patient.email && <p>Email</p>}
                            {encounter?.patient.height && <p>Height</p>}
                            {encounter?.patient.weight && <p>InitialWeight</p>}
                            {encounter?.patient.target_weight && <p>TargetWeight</p>}
                            {encounter?.patient.timeframe && <p>Timeframe</p>}
                            {encounter?.patient.bmi && <p>BMI</p>}
                        </div>
                        <div className="flex flex-col gap-2 font-medium">
                            {encounter?.patient.gender && <p>{encounter?.patient.gender}</p>}
                            {encounter?.patient.age && <p>{encounter?.patient.age}yrs</p>}
                            {encounter?.patient.email && (
                              <p className="truncate max-w-[180px] sm:max-w-none" title={encounter.patient.email}>
                                {encounter.patient.email}
                              </p>
                            )}
                            {encounter?.patient.height && <p>{encounter?.patient.height}</p>}
                            {encounter?.patient.weight && <p>{encounter?.patient.weight}</p>}
                            {encounter?.patient.target_weight && <p>{encounter?.patient.target_weight}</p>}
                            {encounter?.patient.timeframe && <p>{encounter?.patient.timeframe}</p>}
                            {encounter?.patient.bmi && <p>{encounter?.patient.bmi}</p>}
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="initial" className="px-4 pb-4">
                  {encounter?.patient.health_profile ? (
                    <div className="flex flex-col gap-4">
                      {encounter.patient.health_profile.gender && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600 font-normal">Gender</span>
                          <span className="text-base text-gray-800 font-semibold">
                            {encounter.patient.health_profile.gender}
                          </span>
                        </div>
                      )}
                      
                      {(encounter.patient.health_profile.height || encounter.patient.health_profile.weight) && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600 font-normal">
                            What is your current height and weight?
                          </span>
                          <span className="text-base text-gray-800 font-semibold">
                            {[encounter.patient.health_profile.height, encounter.patient.health_profile.weight]
                              .filter(Boolean)
                              .join(" / ")}
                          </span>
                        </div>
                      )}
                      
                      {(encounter.patient.health_profile.target_weight || encounter.patient.health_profile.timeframe) && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600 font-normal">
                            Target weight goal, and in how many months do you want to achieve it?
                          </span>
                          <span className="text-base text-gray-800 font-semibold">
                            {[encounter.patient.health_profile.target_weight, encounter.patient.health_profile.timeframe]
                              .filter(Boolean)
                              .join(" in ")}
                          </span>
                        </div>
                      )}
                      
                      {encounter.patient.health_profile.planned_events && encounter.patient.health_profile.planned_events.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600 font-normal">
                            Events or trips planned that are motivating your weight loss goal?
                          </span>
                          <span className="text-base text-gray-800 font-semibold">
                            {Array.isArray(encounter.patient.health_profile.planned_events)
                              ? encounter.patient.health_profile.planned_events.join(", ")
                              : formatValue(encounter.patient.health_profile.planned_events)}
                          </span>
                        </div>
                      )}
                      
                      {encounter.patient.health_profile.glp_intake && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600 font-normal">
                            Have you ever been on a GLP-1 medication (Wegovy, Ozempic, Zepbound, Trizepatide, Semaglutide, or Mounjaro)?
                          </span>
                          <span className="text-base text-gray-800 font-semibold">
                            {encounter.patient.health_profile.glp_intake}
                          </span>
                        </div>
                      )}
                      
                      {encounter.patient.health_profile.is_diabetic && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600 font-normal">
                            Do you have a personal or family history of any of the following conditions?
                          </span>
                          <span className="text-base text-gray-800 font-semibold">
                            Diabetes - {encounter.patient.health_profile.is_diabetic}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      <p className="text-sm text-gray-600">No health profile available at the moment.</p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="supplementary" className="px-4 pb-4">
                {encounter?.patient.supplementary_health_assessment &&
                      Object.keys(encounter?.patient.supplementary_health_assessment).length > 0 ? (
                      <div className="flex flex-col gap-4">
                        {Object.entries(encounter?.patient.supplementary_health_assessment).map(
                          ([key, value]) => (
                            <div key={key} className="flex flex-col gap-1">
                              <span className="text-sm text-gray-600 font-normal">
                                {key}
                              </span>
                              <span className="text-base text-gray-800 font-semibold">
                                {formatValue(value)}
                              </span>
                            </div>
                          )
                        )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-4">
                              <p className="text-sm text-gray-600">No supplementary questions available at the moment.</p>
                            </div>
                          )}
                </TabsContent>
            </Tabs>
        </div>
                            <div className="w-full flex flex-col sm:flex-row gap-4 bg-[#EBF0FB] p-4 rounded-[8px]">
                            <div className="flex flex-col space-y-1 flex-1">
                            <p className="text-[#464B55] text-xs">Selected Medication:</p>
                            <p className="text-[#1A202C] text-sm font-medium">{encounter?.patient.medical_plan?.medication.name || ""}</p>
                            </div>
                            {encounter?.patient.medical_plan?.medication.name !== "Zepbound" && (
                              <div className="flex flex-col space-y-1 flex-1">
                                <p className="text-[#464B55] text-xs">Selected Vitamin:</p>
                                <p className="text-[#1A202C] text-sm font-medium">{encounter?.patient.medical_plan?.vitamin.name || ""}</p>
                              </div>
                            )}
                            <Link
                                onClick={() => onOpenChange?.(false)}
                                className={cn(buttonVariants({ variant: "outline" }), "w-full sm:w-fit text-white rounded-[10px] bg-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    program,
                                    id: encounter.patient.id
                                }}
                                search={{
                                    name: encounter.patient.name.replace(/\s+/g, ""),
                                    sessionId: encounter?.session?.session_id || ""
                                }}
                            >
                                Review Patient Log
                            </Link>
                        </div>
      </div>

    )
}
